// Detalle de una sesión: cabecera + tabla alumnos con marcado de asistencia + card notas.
// Email/tlf ya vienen ofuscados del backend.
//
// SS7.3: por cada alumno 2 botones inline ("Vino" / "No vino") y pill que muestra
// el estado actual de asistencia. Botón "Marcar todos como vino" en cabecera para
// los que aún no estén marcados. POST optimista sin recarga manual.
// SS7.4: card de notas debajo de la tabla. Textarea + Guardar. Si vacío, borra.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, AlertCircle, Loader2, Calendar, Clock, MapPin, Users, Mail, Phone, CreditCard,
  Check, X, CheckCheck, NotebookPen,
} from 'lucide-react';

import {
  getProfesorClaseAlumnos, postProfesorAsistencia,
  getProfesorClaseNotas, putProfesorClaseNotas,
} from '../../lib/profesorApi';

const NOTA_MAX_LEN = 2000;

function formatRelativeTimeAgo(iso, lang) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return null;
  const min = Math.floor(ms / 60000);
  if (min < 1) return { value: 0, unit: 'second' };
  if (min < 60) return { value: -min, unit: 'minute' };
  const hr = Math.floor(min / 60);
  if (hr < 24) return { value: -hr, unit: 'hour' };
  const d = Math.floor(hr / 24);
  if (d < 30) return { value: -d, unit: 'day' };
  const mo = Math.floor(d / 30);
  return { value: -mo, unit: 'month' };
}

function rtfFormat(rel, lang) {
  if (!rel) return '';
  const locales = { es: 'es-ES', en: 'en-GB', ru: 'ru-RU', fr: 'fr-FR' };
  try {
    const rtf = new Intl.RelativeTimeFormat(locales[lang] || 'es-ES', { numeric: 'auto' });
    return rtf.format(rel.value, rel.unit);
  } catch {
    return '';
  }
}

function formatFechaLarga(iso, lang) {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const locales = { es: 'es-ES', en: 'en-GB', ru: 'ru-RU', fr: 'fr-FR' };
  const f = new Intl.DateTimeFormat(locales[lang] || 'es-ES', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  const s = f.format(date);
  return s.charAt(0).toLocaleUpperCase(locales[lang]) + s.slice(1);
}

// 'asistio' → vino; 'no_asistio_*' → no vino; null → pendiente.
function asistenciaSimplificada(estadoDetallado) {
  if (estadoDetallado === 'asistio') return 'vino';
  if (estadoDetallado && estadoDetallado.startsWith('no_asistio')) return 'no_vino';
  return null;
}

// ---------------------------------------------------------------------------
// Sub-componente: card de notas de la sesión.
// ---------------------------------------------------------------------------
function NotasSesion({ sesionId, lang }) {
  const { t } = useTranslation();
  const [nota, setNota] = useState(null);          // {contenido, autor_rol, updated_at} o null
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [okMsg, setOkMsg] = useState(null);
  const [errMsg, setErrMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getProfesorClaseNotas(sesionId, lang)
      .then((d) => {
        if (cancelled) return;
        setNota(d.nota || null);
        setDraft(d.nota?.contenido || '');
      })
      .catch((e) => { if (!cancelled) setErrMsg(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sesionId, lang]);

  const dirty = (nota?.contenido || '') !== draft.trim();
  const tooLong = draft.trim().length > NOTA_MAX_LEN;

  const guardar = async () => {
    setOkMsg(null);
    setErrMsg(null);
    setSaving(true);
    try {
      const resp = await putProfesorClaseNotas(sesionId, draft, lang);
      setNota(resp.nota || null);
      setDraft(resp.nota?.contenido || '');
      const accion = resp.accion || 'actualizada';
      setOkMsg(t('profesor.clase.notas.guardado.' + accion, { defaultValue: t('profesor.clase.notas.guardado.actualizada') }));
      setTimeout(() => setOkMsg(null), 3500);
    } catch (e) {
      const tradKey = 'profesor.clase.notas.errores.' + (e.code || 'generico');
      const traducido = t(tradKey, { defaultValue: '' });
      setErrMsg(traducido || e.message || t('profesor.clase.notas.errores.generico'));
    } finally {
      setSaving(false);
    }
  };

  const ultimaEdicion = nota?.updated_at
    ? rtfFormat(formatRelativeTimeAgo(nota.updated_at), lang)
    : null;

  return (
    <section className="admin-section profesor-notas-section">
      <div className="admin-section-head">
        <h2>
          <NotebookPen size={18} strokeWidth={1.5} style={{ verticalAlign: '-3px', marginRight: 6 }} />
          {t('profesor.clase.notas.titulo')}
        </h2>
        {ultimaEdicion && !loading && (
          <span className="profesor-notas-meta">{t('profesor.clase.notas.ultima_edicion', { tiempo: ultimaEdicion })}</span>
        )}
      </div>

      {loading ? (
        <p className="admin-empty"><Loader2 size={14} className="spin" /> {t('admin.cargando')}</p>
      ) : (
        <>
          <textarea
            className="profesor-notas-textarea"
            rows={6}
            maxLength={NOTA_MAX_LEN + 50}
            placeholder={t('profesor.clase.notas.placeholder')}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={saving}
          />
          <div className="profesor-notas-foot">
            <span className={'profesor-notas-counter' + (tooLong ? ' over' : '')}>
              {draft.trim().length} / {NOTA_MAX_LEN}
            </span>
            <button
              type="button"
              className="btn-primary profesor-notas-btn-guardar"
              onClick={guardar}
              disabled={saving || !dirty || tooLong}
            >
              {saving ? (
                <><Loader2 size={14} className="spin" strokeWidth={1.5} /> {t('profesor.clase.notas.guardando')}</>
              ) : (
                t('profesor.clase.notas.btn_guardar')
              )}
            </button>
          </div>

          {okMsg && (
            <div className="admin-msg-ok profesor-asis-banner">
              <Check size={14} strokeWidth={1.75} /> {okMsg}
            </div>
          )}
          {errMsg && (
            <div className="modal-error-general profesor-asis-banner">
              <AlertCircle size={14} strokeWidth={1.75} /> {errMsg}
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default function ProfesorClase() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || 'es';
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);    // reserva_id en flight (botones inline)
  const [savingAll, setSavingAll] = useState(false);
  const [okMsg, setOkMsg] = useState(null);
  const [errMsg, setErrMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getProfesorClaseAlumnos(id, lang)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, lang]);

  const aplicarAsistencia = async (body, contextId = null) => {
    setOkMsg(null);
    setErrMsg(null);
    if (contextId) setSavingId(contextId);
    if (body.marcar_todos_vinieron) setSavingAll(true);
    try {
      const resp = await postProfesorAsistencia(id, body, lang);
      setData(resp);
      setOkMsg(t('profesor.clase.asistencia.guardado_ok', { count: resp.asistencias_aplicadas || 0 }));
      setTimeout(() => setOkMsg(null), 3500);
    } catch (e) {
      const tradKey = 'profesor.clase.asistencia.errores.' + (e.code || 'generico');
      const traducido = t(tradKey, { defaultValue: '' });
      setErrMsg(traducido || e.message || t('profesor.clase.asistencia.errores.generico'));
    } finally {
      setSavingId(null);
      setSavingAll(false);
    }
  };

  const marcarUno = (reservaId, asistio) =>
    aplicarAsistencia({ asistencias: [{ reserva_id: reservaId, asistio }] }, reservaId);

  const marcarTodosVinieron = () =>
    aplicarAsistencia({ marcar_todos_vinieron: true });

  if (loading) {
    return <div className="admin-loading"><Loader2 size={28} className="spin" /> <span>{t('admin.cargando')}</span></div>;
  }
  if (error) {
    return (
      <div className="admin-page">
        <button type="button" className="profesor-volver" onClick={() => navigate('/profesor')}>
          <ArrowLeft size={16} strokeWidth={1.5} /> {t('profesor.clase.volver')}
        </button>
        <div className="admin-error"><AlertCircle size={20} /><span>{error.message || t('profesor.clase.error_carga')}</span></div>
      </div>
    );
  }
  if (!data) return null;

  const s = data.sesion;
  const alumnos = data.alumnos || [];
  const pendientes = alumnos.filter((a) => !a.asistencia_actual && a.estado !== 'cancelada').length;

  return (
    <div className="admin-page">
      <button type="button" className="profesor-volver" onClick={() => navigate('/profesor')}>
        <ArrowLeft size={16} strokeWidth={1.5} /> {t('profesor.clase.volver')}
      </button>

      <header className="admin-page-head">
        <h1>{s.clase_tipo?.nombre || t('profesor.clase.titulo_fallback')}</h1>
        <p className="admin-page-sub">{formatFechaLarga(s.fecha_clase, lang)}</p>
      </header>

      <section className="profesor-clase-info">
        <div className="profesor-info-item">
          <Clock size={16} strokeWidth={1.5} />
          <span>{s.hora_inicio} – {s.hora_fin}</span>
        </div>
        {s.sala && (
          <div className="profesor-info-item">
            <MapPin size={16} strokeWidth={1.5} />
            <span>{s.sala}{s.sala_overrideada && ' ★'}</span>
          </div>
        )}
        <div className="profesor-info-item">
          <Calendar size={16} strokeWidth={1.5} />
          <span>{t('profesor.formato.' + (s.formato || 'presencial'))}</span>
        </div>
        <div className="profesor-info-item">
          <Users size={16} strokeWidth={1.5} />
          <span>{data.total_alumnos} {t('profesor.clase.alumnos_inscritos')}</span>
        </div>
        {s.rol_en_sesion === 'asignado' && (
          <span className="profesor-pill profesor-pill-asignado">{t('profesor.dashboard.pill_asignado')}</span>
        )}
      </section>

      <section className="admin-section">
        <div className="admin-section-head">
          <h2>{t('profesor.clase.alumnos_titulo')}</h2>
          {pendientes > 0 && (
            <button
              type="button"
              className="btn-secundario profesor-btn-todos"
              onClick={marcarTodosVinieron}
              disabled={savingAll}
            >
              {savingAll ? (
                <><Loader2 size={14} className="spin" strokeWidth={1.5} /> {t('profesor.clase.asistencia.guardando')}</>
              ) : (
                <><CheckCheck size={14} strokeWidth={1.5} /> {t('profesor.clase.asistencia.marcar_todos', { count: pendientes })}</>
              )}
            </button>
          )}
        </div>

        {okMsg && (
          <div className="admin-msg-ok profesor-asis-banner">
            <Check size={14} strokeWidth={1.75} /> {okMsg}
          </div>
        )}
        {errMsg && (
          <div className="modal-error-general profesor-asis-banner">
            <AlertCircle size={14} strokeWidth={1.75} /> {errMsg}
          </div>
        )}

        {alumnos.length === 0 ? (
          <p className="admin-empty">{t('profesor.clase.sin_alumnos')}</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('profesor.clase.col_nombre')}</th>
                <th>{t('profesor.clase.col_email')}</th>
                <th>{t('profesor.clase.col_telefono')}</th>
                <th>{t('profesor.clase.col_pago')}</th>
                <th>{t('profesor.clase.asistencia.col_asistencia')}</th>
              </tr>
            </thead>
            <tbody>
              {alumnos.map((a) => {
                const simpl = asistenciaSimplificada(a.asistencia_actual);
                const isSaving = savingId === a.reserva_id;
                return (
                  <tr key={a.reserva_id} className={simpl ? 'profesor-row-marcada' : ''}>
                    <td>{a.nombre}</td>
                    <td className="profesor-celda-tenue">
                      {a.email_ofuscado ? (
                        <span className="profesor-icon-text"><Mail size={12} strokeWidth={1.5} />{a.email_ofuscado}</span>
                      ) : '—'}
                    </td>
                    <td className="profesor-celda-tenue">
                      {a.telefono_ofuscado ? (
                        <span className="profesor-icon-text"><Phone size={12} strokeWidth={1.5} />{a.telefono_ofuscado}</span>
                      ) : '—'}
                    </td>
                    <td>
                      {a.tipo_pago ? (
                        <span className="profesor-icon-text">
                          <CreditCard size={12} strokeWidth={1.5} />
                          {t('profesor.clase.metodo.' + a.tipo_pago.metodo, { defaultValue: a.tipo_pago.metodo })}
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      <div className="profesor-asis-controls">
                        <button
                          type="button"
                          className={'profesor-btn-asis profesor-btn-vino' + (simpl === 'vino' ? ' active' : '')}
                          onClick={() => marcarUno(a.reserva_id, true)}
                          disabled={isSaving}
                          title={t('profesor.clase.asistencia.btn_vino')}
                        >
                          {isSaving ? <Loader2 size={12} className="spin" strokeWidth={1.5} /> : <Check size={12} strokeWidth={2} />}
                          <span>{t('profesor.clase.asistencia.btn_vino')}</span>
                        </button>
                        <button
                          type="button"
                          className={'profesor-btn-asis profesor-btn-no-vino' + (simpl === 'no_vino' ? ' active' : '')}
                          onClick={() => marcarUno(a.reserva_id, false)}
                          disabled={isSaving}
                          title={t('profesor.clase.asistencia.btn_no_vino')}
                        >
                          {isSaving ? <Loader2 size={12} className="spin" strokeWidth={1.5} /> : <X size={12} strokeWidth={2} />}
                          <span>{t('profesor.clase.asistencia.btn_no_vino')}</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <NotasSesion sesionId={s.id} lang={lang} />

      <p className="profesor-aviso-privacidad">{t('profesor.clase.aviso_privacidad')}</p>
    </div>
  );
}
